import { isLocale, type Locale } from '@emr/shared';

/**
 * The patient portal's own client-only language preference — deliberately **not**
 * `web/src/i18n/useSyncAccountLocale.ts`/`LanguageMenu.tsx`, which persist to
 * `GET/PUT /api/profile/locale`, a staff-session-gated endpoint (`web/src/api/locale.ts`) with no
 * patient-portal equivalent. Building a patient-facing locale-preference route is backend work this
 * task is explicitly scoped not to add (`docs/decisions/patient-portal-frontend-scope.md` flags this
 * as the one real gap found while building this surface) — so v1 stores the choice in this browser's
 * own `localStorage` instead, the same mechanism `theme/preference.ts` and
 * `format/dateTimePreference.ts` already use for a preference with no server-side home. `null` means
 * "no stored preference" — `i18n/index.ts`'s own `applyLocale` already falls back to
 * `navigator.languages` and then English for that case, so this module never needs its own fallback
 * logic.
 */

const STORAGE_KEY = 'emr:portal-locale';

export function readPortalLocalePreference(storage: Pick<Storage, 'getItem'>): Locale | null {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw !== null && isLocale(raw) ? raw : null;
  } catch {
    // Private-browsing storage access can throw outright rather than just returning `null`.
    return null;
  }
}

export function writePortalLocalePreference(
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
  locale: Locale | null,
): void {
  try {
    if (locale === null) storage.removeItem(STORAGE_KEY);
    else storage.setItem(STORAGE_KEY, locale);
  } catch {
    // Best-effort — a quota or private-browsing error must not crash the picker; the choice still
    // applies for the rest of this session via `applyLocale`, it just will not survive a reload.
  }
}
